import dayjs from 'dayjs'
import type { BoardItem } from '@/pages/Boards/hooks'
import { MONO, QUEUE_COLOR } from './queueTheme'

type StatDef = {
  label: string
  value: number
  /** Red when the number is the one that means someone is behind. */
  alert?: boolean
}

/**
 * The four queue KPIs as a single banded strip, matching the Care Journeys
 * board: mono uppercase labels, large tabular numerals, and colour reserved for
 * the count that actually needs attention — so the strip reads as data rather
 * than as four competing cards.
 */
export const QueueStats = ({
  todoItems,
  completedItems,
  priorityColumnId,
  dateColumnId,
}: {
  todoItems: BoardItem[]
  completedItems: BoardItem[]
  // Cell values are keyed by column id; resolved by the caller from the board.
  priorityColumnId?: string
  dateColumnId?: string
}) => {
  const today = dayjs().startOf('day')
  const overdue = todoItems.filter((item) => {
    const due = dateColumnId ? item.values[dateColumnId] : undefined
    return typeof due === 'string' && dayjs(due).startOf('day').isBefore(today)
  }).length
  const highPriority = priorityColumnId
    ? todoItems.filter((item) => item.values[priorityColumnId] === 'High').length
    : 0

  const stats: StatDef[] = [
    { label: 'Active tasks', value: todoItems.length },
    { label: 'High priority', value: highPriority },
    { label: 'Overdue', value: overdue, alert: overdue > 0 },
    { label: 'Completed', value: completedItems.length },
  ]

  return (
    <div
      className="grid grid-cols-1 overflow-hidden rounded-[14px] bg-white sm:grid-cols-2 xl:grid-cols-4"
      style={{ border: `1px solid ${QUEUE_COLOR.border}` }}
    >
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className="flex flex-col gap-[7px] px-8 py-5"
          style={{
            borderLeft: index === 0 ? undefined : `1px solid ${QUEUE_COLOR.hairline}`,
            background: QUEUE_COLOR.surfaceAlt,
          }}
        >
          <div
            className="text-[10.5px] tracking-[0.12em] uppercase"
            style={{ fontFamily: MONO, color: QUEUE_COLOR.muted }}
          >
            {stat.label}
          </div>
          <div
            className="text-[30px] leading-none font-medium tracking-[-0.02em] tabular-nums"
            style={{ color: stat.alert ? QUEUE_COLOR.late : QUEUE_COLOR.ink }}
          >
            {stat.value}
          </div>
        </div>
      ))}
    </div>
  )
}
